// EmailSettings.jsx — which order emails the shop sends, and whether they work.
//
// Two halves, and the second is the one that earns the screen. The switches
// say what the shop INTENDS; the panel beside them says what is actually
// happening — whether SMTP is configured at all, how many messages are
// waiting, how many gave up. A settings page without that is a page that
// looks fine while nothing has been delivered for a week.
//
// The test send is here for the same reason. Every other way of checking
// costs a real order, so without it the first person to discover the password
// is wrong is a customer who never got their confirmation.
import React, { useCallback, useEffect, useState } from "react";
import api from "../../../api/axios";
import { PageShell, Button, Toggle, Field, Pill } from "../component/AdminUi";

/* The three moments worth an email, and why each one is worth it. The copy
   matters: an operator deciding whether to switch something off needs to know
   what the customer stops receiving, not the name of a database column. */
const EVENTS = [
  {
    key: "onCreated",
    title: "Order placed",
    blurb: "A confirmation the moment an order is taken, with the items and the total.",
  },
  {
    key: "onOutForDelivery",
    title: "Out for delivery",
    blurb: "Sent when the order leaves the shop, with the delivery address on it.",
  },
  {
    key: "onCancelled",
    title: "Cancelled",
    blurb:
      "Sent if an order is cancelled. Mentions a refund only when the customer actually paid.",
  },
];

export default function EmailSettings() {
  const [form, setForm] = useState(null);
  const [queue, setQueue] = useState(null);
  const [smtp, setSmtp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);          // { tone, text }
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setForm(data.data || {});
      setQueue(data.queue || null);
      setSmtp(data.smtp || null);
      if (!testTo && data.smtp?.user) setTestTo(data.smtp.user);
    } catch (e) {
      setNote({ tone: "bad", text: readError(e, "Could not load the email settings.") });
    } finally {
      setLoading(false);
    }
  }, [testTo]);

  useEffect(() => {
    load();
    // Deliberately once. The queue counts are refreshed by Save and by the
    // test send, which are the only two things on this screen that change
    // them; a poll here would be a request every few seconds for a number
    // that moves once a day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      await api.put("/admin/email-settings", {
        enabled: form.enabled,
        onCreated: form.onCreated,
        onOutForDelivery: form.onOutForDelivery,
        onCancelled: form.onCancelled,
        fromName: form.fromName ?? "",
        replyTo: form.replyTo ?? "",
        supportPhone: form.supportPhone ?? "",
        supportEmail: form.supportEmail ?? "",
        logoUrl: form.logoUrl ?? "",
      });
      setNote({ tone: "ok", text: "Saved." });
      await load();
    } catch (e) {
      setNote({ tone: "bad", text: readError(e, "Could not save.") });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setNote(null);
    try {
      const { data } = await api.post("/admin/email-settings/test", { to: testTo.trim() });
      setNote({ tone: "ok", text: data.message || "Sent." });
    } catch (e) {
      /* The provider's own words, not ours. "Username and Password not
         accepted" is the entire diagnosis for a Gmail account given a login
         password instead of an app password, and burying it behind "could not
         send" costs somebody an afternoon. */
      setNote({ tone: "bad", text: readError(e, "Could not send.") });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <PageShell title="Order emails"><p className="ui-muted">Loading…</p></PageShell>;
  if (!form) return <PageShell title="Order emails"><p className="ui-muted">Settings unavailable.</p></PageShell>;

  const off = !form.enabled;

  return (
    <PageShell
      title="Order emails"
      count="What the shop tells a customer, and when"
      actions={
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      }
    >
      {note ? (
        <div className={"ui-banner " + (note.tone === "ok" ? "is-ok" : "is-bad")}>{note.text}</div>
      ) : null}

      <div className="ui-grid-2">
        {/* ── left: what gets sent ─────────────────────────────────── */}
        <div className="ui-card">
          <div className="ui-card__row">
            <div>
              <h3 className="ui-card__title">Send order emails</h3>
              <p className="ui-muted">
                The master switch. Off means nothing new is queued; anything already
                waiting still goes out.
              </p>
            </div>
            <Toggle on={!!form.enabled} onChange={(v) => set("enabled", v)} label="Send order emails" />
          </div>

          <hr className="ui-rule" />

          {EVENTS.map((e) => (
            <div className="ui-card__row" key={e.key}>
              <div>
                <h4 className="ui-card__sub">{e.title}</h4>
                <p className="ui-muted">{e.blurb}</p>
              </div>
              <Toggle
                on={!!form[e.key]}
                disabled={off}
                onChange={(v) => set(e.key, v)}
                label={e.title}
              />
            </div>
          ))}

          {off ? (
            <p className="ui-muted ui-note">
              Every event above is ignored while the master switch is off.
            </p>
          ) : null}
        </div>

        {/* ── right: does it actually work ─────────────────────────── */}
        <div className="ui-card">
          <h3 className="ui-card__title">Delivery</h3>

          <div className="ui-kv">
            <span>Mail server</span>
            <span>
              {smtp?.configured ? (
                <Pill tone="leaf">{smtp.host}:{smtp.port}</Pill>
              ) : (
                <Pill tone="gold">Not configured</Pill>
              )}
            </span>
          </div>
          <div className="ui-kv">
            <span>Sends as</span>
            <span>{smtp?.from || "—"}</span>
          </div>
          <div className="ui-kv">
            <span>Waiting</span>
            <span>{queue?.queued ?? 0}</span>
          </div>
          <div className="ui-kv">
            <span>Sent</span>
            <span>{queue?.sent ?? 0}</span>
          </div>
          <div className="ui-kv">
            <span>Gave up</span>
            <span>
              {queue?.failed > 0 ? <Pill tone="gold">{queue.failed}</Pill> : (queue?.failed ?? 0)}
            </span>
          </div>

          <hr className="ui-rule" />

          <h4 className="ui-card__sub">Send a test</h4>
          <p className="ui-muted">
            Proves this server can reach the mail provider and sign in. Costs nothing
            and touches no order.
          </p>
          <div className="ui-row-gap">
            <input
              className="ui-input"
              value={testTo}
              placeholder="someone@example.com"
              onChange={(e) => setTestTo(e.target.value)}
            />
            <Button onClick={sendTest} disabled={testing || !smtp?.configured}>
              {testing ? "Sending…" : "Send test"}
            </Button>
          </div>
          {!smtp?.configured ? (
            <p className="ui-muted ui-note">
              The mail server credentials live in the server's environment, not here —
              a password this screen could read back is a password this screen leaks.
            </p>
          ) : null}
        </div>
      </div>

      {/* ── what the message looks like ───────────────────────────── */}
      <div className="ui-card">
        <h3 className="ui-card__title">How the message reads</h3>
        <p className="ui-muted">
          These appear on every order email. The logo must be a public https address —
          a mail client fetches it as a stranger, days later.
        </p>

        <div className="ui-grid-2">
          <TextField label="From name" value={form.fromName ?? ""} onChange={(v) => set("fromName", v)} placeholder="MilliyO" />
          <TextField label="Reply-to address" value={form.replyTo ?? ""} onChange={(v) => set("replyTo", v)} placeholder="Blank: replies go to the sending address" />
          <TextField label="Support phone" value={form.supportPhone ?? ""} onChange={(v) => set("supportPhone", v)} placeholder="Shown in the footer" />
          <TextField label="Support email" value={form.supportEmail ?? ""} onChange={(v) => set("supportEmail", v)} placeholder="Shown in the footer" />
          <TextField label="Logo URL" value={form.logoUrl ?? ""} onChange={(v) => set("logoUrl", v)} placeholder="Blank: show the shop name as text" />
        </div>

        {form.logoUrl ? (
          <div className="ui-logo-preview">
            <img src={form.logoUrl} alt="Logo as it will appear" />
            <span className="ui-muted">
              Shown at 132px wide on a dark banner. If this box is empty, the address is wrong
              and customers will see the shop name instead.
            </span>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}

/* Field only draws the label and the hint; the control is its child. A tiny
   wrapper keeps the five settings below readable instead of five copies of
   the same input markup. */
function TextField({ label, value, onChange, placeholder, hint }) {
  return (
    <Field label={label} hint={hint}>
      <input
        className="ui-input"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/* The server's message when it sent one — it is always more specific than
   anything this file could invent. */
function readError(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}
